/**
 *
 * @class BatchCell
 *
 * helper to read a batch plan file and be able to pull out the name of the batch.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin
 * @date      February 2022
 *
 */
import {Logger} from "@/util/logger";

export class BatchPlan {

  /** the name of the plan */
  public _name: string     = '';

  /** batch key */
  public _batch: string    = '';

  /** corridor ID */
  public _corridor: number = 0;

  public _start: string    = '';

  public _end: string      = '';

  public _author: string   = '';

  public _created: string  = '';

  /** standard constructor */
  public constructor(text: string) {

    /* is this even remotely a plan file? */

    if (text.length > 8192*4) {

      /* It's too big, likely some kind of binary file */

      return ;
    }

    if ( !text.includes('corridorplan_data') || !text.includes('schema') ) {

      /* not a plan file */

      return ;
    }

    /* try to parse... */

    let data: any = null;

    try {

      Logger.debug(`[BatchPlan] parsing plan sz=${text.length}...`);

      data = JSON.parse(text);

    } catch (error) {

      Logger.error(`[BatchPlan] could not parse given JSON.`);

      return ;
    }

    /* valid structure? */

    if (!('corridorplan_data' in data)) {

      return ;
    }

    const cd: any = data.corridorplan_data;

    if (!('corridorplan' in cd)) {

      return ;
    }

    const cp = cd.corridorplan;

    if (!('schema' in cp)) {

      return ;
    }

    const schema = cp.schema;

    /* get the column mapping ... */

    Logger.debug(`[BatchPlan] plan structure is ok, pulling column mapping...`);

    let nameMap: Map<string, number> = new Map<string, any>();
    let  colMap: Map<number, string> = new Map<number, any>();

    schema.fields.forEach((col: any) => {

      let   name: string = col.name;
      let colNum: number = col.col - 1;

      nameMap.set(name, colNum);
      colMap.set(colNum, name);

    });

    const rows: any[] = cp.data;
    const row: any[]  = rows[0];

    this._batch    = row[nameMap.get('batch_id')];
    this._corridor = row[nameMap.get('corridor_plan_id')];
    this._name     = row[nameMap.get('name')];
    this._start    = row[nameMap.get('start_date')];
    this._end      = row[nameMap.get('end_date')];
    this._author   = row[nameMap.get('created_by')];
    this._created  = row[nameMap.get('created_on')];

  }

  /** fetch the name of the batch plan */
  public name(): string {

    return this._name;
  }

  public batch(): string {

    return this._batch;
  }

  public corridor(): number {

    return this._corridor;
  }

  public start(): string {

    return this._start;
  }

  public end(): string {

    return this._start;
  }

  public author(): string {

    return this._author;
  }

  public created(): string {

    return this._author;
  }

  /** helper to auto-sense a batch plan name from the batch plan */
  public static planName(text: string): string {

    const plan: BatchPlan = new BatchPlan(text);

    return plan.name();
  }
}
